import { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import useTheme from "@mui/material/styles/useTheme";

import { MANAGED_CARE_PAYMENT_SOURCE } from "constants/patients";
import { TinyInsurancePolicy } from "services/types/insurance-policies.type";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";
import { formatMoney } from "utils/common";

type useBalanceDisplayProps = {
  totalBalance: number;
  insurerResponsibilityBalance: number;
  patientResponsibilityBalance: number;
  managedCareResponsibilityBalance: number;
  managedCarePlan?: ManagedCarePlan;
  insurancePolicy?: TinyInsurancePolicy;
  paymentSourceType: number;
  showDecimal?: boolean;
  prefixText?: string;
};

const getBalanceColor = (
  value: number,
  balanceColors: {
    positive: string;
    balanced: string;
    negative: string;
  }
) => {
  if (value === 0) return balanceColors.balanced;
  if (value < 0) return balanceColors.positive;
  return balanceColors.negative;
};

export default function useBalanceDisplay({
  totalBalance,
  insurerResponsibilityBalance,
  patientResponsibilityBalance,
  managedCareResponsibilityBalance,
  managedCarePlan,
  insurancePolicy,
  paymentSourceType,
  showDecimal = false,
  prefixText = ""
}: useBalanceDisplayProps) {
  const { palette } = useTheme();

  const [balanceAnchorEl, setBalanceAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const balanceColors = {
    positive: palette.success.light, // green
    balanced: palette.grey.A100, // light gray
    negative: palette.patientHeader.dark // red
  };

  const totalBalanceColor = getBalanceColor(totalBalance, balanceColors);

  const fractionDigits = showDecimal ? 2 : 0;

  const totalBalanceText = useMemo(
    () =>
      [prefixText, formatMoney(totalBalance, fractionDigits, fractionDigits)]
        .join(" ")
        .trim(),
    [totalBalance, fractionDigits, prefixText]
  );
  const insurerResponsibilityBalanceColor = getBalanceColor(
    insurerResponsibilityBalance,
    balanceColors
  );

  const insurerResponsibilityBalanceText = useMemo(
    () =>
      [
        prefixText,
        formatMoney(
          insurerResponsibilityBalance,
          fractionDigits,
          fractionDigits
        )
      ]
        .join(" ")
        .trim(),
    [insurerResponsibilityBalance, fractionDigits, prefixText]
  );
  const managedCareResponsibilityBalanceColor = getBalanceColor(
    managedCareResponsibilityBalance,
    balanceColors
  );
  const managedCareResponsibilityBalanceText = useMemo(
    () =>
      [
        prefixText,
        formatMoney(
          managedCareResponsibilityBalance,
          fractionDigits,
          fractionDigits
        )
      ]
        .join(" ")
        .trim(),
    [managedCareResponsibilityBalance, fractionDigits, prefixText]
  );
  const patientResponsibilityBalanceColor = getBalanceColor(
    patientResponsibilityBalance,
    balanceColors
  );
  const patientResponsibilityBalanceText = useMemo(
    () =>
      [
        prefixText,
        formatMoney(
          patientResponsibilityBalance,
          fractionDigits,
          fractionDigits
        )
      ]
        .join(" ")
        .trim(),
    [patientResponsibilityBalance, fractionDigits, prefixText]
  );

  const handleShowBalanceExpander = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setBalanceAnchorEl(event.currentTarget);
    },
    []
  );

  const handleHideBalanceExpander = useCallback(() => {
    setBalanceAnchorEl(null);
  }, []);

  const additionalBalance = useMemo(() => {
    if (paymentSourceType === MANAGED_CARE_PAYMENT_SOURCE) {
      return {
        label: "Managed Care",
        value: managedCareResponsibilityBalanceText,
        color: managedCareResponsibilityBalanceColor
      };
    }
    return {
      label: "Insurer",
      value: insurerResponsibilityBalanceText,
      color: insurerResponsibilityBalanceColor
    };
  }, [
    paymentSourceType,
    insurerResponsibilityBalanceText,
    insurerResponsibilityBalanceColor,
    managedCareResponsibilityBalanceText,
    managedCareResponsibilityBalanceColor
  ]);

  const balanceExpanderContent = useMemo(() => {
    if (
      paymentSourceType === MANAGED_CARE_PAYMENT_SOURCE &&
      insurerResponsibilityBalance !== 0
    ) {
      return (
        <>
          <Grid container sx={{ p: 0.5 }}>
            <Grid item xs={4}>
              Insurer
            </Grid>
            <Grid item xs={8}>
              {insurancePolicy?.insurer?.name}
            </Grid>
          </Grid>
          <Grid
            container
            sx={{
              mt: 0.5
            }}
          >
            <Grid
              item
              xs={4}
              sx={{
                p: 0.5,
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                background: insurerResponsibilityBalanceColor
              }}
            >
              Balance
            </Grid>
            <Grid item xs={8} sx={{ mt: 0.5, ml: -0.1 }}>
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  p: 0.5,
                  pr: 1,
                  borderTopRightRadius: "5px",
                  borderBottomRightRadius: "5px",
                  background: insurerResponsibilityBalanceColor
                }}
              >
                {insurerResponsibilityBalanceText}
              </Box>
            </Grid>
          </Grid>
        </>
      );
    } else if (
      paymentSourceType !== MANAGED_CARE_PAYMENT_SOURCE &&
      managedCareResponsibilityBalance !== 0
    ) {
      return (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            Managed Care Plan
          </Grid>
          <Grid item xs={8}>
            {managedCarePlan?.name}
          </Grid>
          <Grid item xs={4}>
            Balance
          </Grid>
          <Grid item xs={8} sx={{ fontWeight: 700 }}>
            {managedCareResponsibilityBalanceText}
          </Grid>
        </Grid>
      );
    }
  }, [
    insurancePolicy?.insurer?.name,
    insurerResponsibilityBalance,
    insurerResponsibilityBalanceColor,
    insurerResponsibilityBalanceText,
    managedCarePlan?.name,
    managedCareResponsibilityBalance,
    managedCareResponsibilityBalanceText,
    paymentSourceType
  ]);

  return {
    totalBalanceText,
    totalBalanceColor,

    patientResponsibilityBalanceColor,
    patientResponsibilityBalanceText,
    additionalBalance,

    handleShowBalanceExpander,
    handleHideBalanceExpander,
    balanceExpanderContent,
    balanceAnchorEl
  };
}
